import {
  useQueries,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getErrorResponseCode } from 'shared'
import { Item, PaymentMethodsType } from 'typings'
import { useFetch } from './useFetch'
import { getSuccessData } from './utils'

type Params = {
  salesChannelsIds: string[]
  options?: Partial<UseQueryOptions<Item[]>>
}

export function filterDuplicates(items: Item[]): Item[] {
  return items.reduce((uniqueItems: Item[], item) => {
    if (!uniqueItems.find((i) => i.id === item.id)) {
      uniqueItems.push(item)
    }
    return uniqueItems
  }, [])
}

export function sortAlphabetically(items: Item[]): Item[] {
  return items.sort((a, b) => a.name.localeCompare(b.name))
}

export const getIds = (items: Item[] | undefined) =>
  items?.map((x) => x.id) ?? []

export function usePaymentMethods(params: Params) {
  const { options, salesChannelsIds } = params
  const { fetcherWrapper } = useFetch()

  async function fetcher(id: string): Promise<Item[]> {
    const url = new URL(
      `/api/proxy/payments/pvt/merchants/payment-systems?salesChannel=${id}`,
      window.location.origin
    )

    return fetcherWrapper(url)
      .then((r) => {
        if (r.status === 404) return Promise.reject('Not found')
        if (!r.ok) return Promise.reject('Error retrieving payment methods')

        return r.json()
      })
      .then((r: PaymentMethodsType) =>
        r.map((x) => ({ id: x.id.toString(), name: x.name }))
      )
  }

  function combineData(data: Item[][]): Item[] {
    const uniqueItems = filterDuplicates(data.flat())
    return sortAlphabetically(uniqueItems)
  }

  return useQueries({
    queries: salesChannelsIds.map((salesChannelId) => ({
      queryKey: ['payment-methods', salesChannelId],
      queryFn: () => fetcher(salesChannelId),
      ...options,
      retry: false,
    })),
    combine: (results) => {
      const successfulData = getSuccessData(results)
      return {
        data: combineData(successfulData),
        isLoading: results.some((r) => r.isLoading),
        hasPermissionError: results.some(
          (r) => getErrorResponseCode(r.error) === 403
        ),
      }
    },
  })
}
